import { Navigate, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";
import type { SessionCapabilities } from "../api/sessionContext";

interface ProtectedRouteProps {
  children: ReactNode;
  /** A tela de troca é acessível tanto para troca obrigatória quanto voluntária. */
  allowPasswordChangeRequired?: boolean;
  /** A própria tela de matrícula precisa ficar acessível com requiresMfaEnrollment=true (@F1-ROUTE-002). */
  allowMfaEnrollmentRequired?: boolean;
}

/**
 * Decide para onde uma rota protegida deve redirecionar, ou null se pode renderizar os filhos. Extraída
 * do componente (fase F1) só para manter a complexidade ciclomática de ProtectedRoute dentro do limite
 * do projeto (lint:complexity) — mesma ordem e mesmas condições de antes: sem sessão → /login; senha
 * pendente → /trocar-senha; MFA pendente → /mfa/matricula. Checagem de MFA vem DEPOIS da de senha: sem
 * senha definitiva não faz sentido pedir matrícula de MFA ainda.
 */
function resolveProtectedRedirect(
  state: ReturnType<typeof useAuth>["state"],
  options: { allowPasswordChangeRequired: boolean; allowMfaEnrollmentRequired: boolean },
): string | null {
  if (state.status === "unauthenticated") return "/login";
  if (state.requiresPasswordChange) return options.allowPasswordChangeRequired ? null : "/trocar-senha";
  return state.requiresMfaEnrollment && !options.allowMfaEnrollmentRequired ? "/mfa/matricula" : null;
}

/**
 * Bloqueia rota sem sessão; força passagem por /trocar-senha quando requires_password_change=true;
 * força passagem por /mfa/matricula quando requires_mfa_enrollment=true (ADR-054, decisão institucional
 * 2 — bloqueio da aplicação inteira, mesmo padrão de requiresPasswordChange).
 */
export function ProtectedRoute({
  children,
  allowPasswordChangeRequired = false,
  allowMfaEnrollmentRequired = false,
}: ProtectedRouteProps) {
  const { state } = useAuth();
  const redirectTo = resolveProtectedRedirect(state, { allowPasswordChangeRequired, allowMfaEnrollmentRequired });
  if (redirectTo) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

export function CapabilityRoute({ children, capability }: { children: ReactNode; capability: keyof SessionCapabilities }) {
  const { state } = useAuth();
  if (state.status !== "authenticated") return <Navigate to="/login" replace />;
  if (state.sessionContextStatus === "loading") return null;
  return state.sessionContext?.capabilities[capability] ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

/** Permite o autoatendimento do titular na edição de contato; qualquer terceiro exige a capability. */
export function SelfOrCapabilityRoute({ children, capability }: { children: ReactNode; capability: keyof SessionCapabilities }) {
  const { memberId } = useParams();
  const { state } = useAuth();
  if (state.status !== "authenticated") return <Navigate to="/login" replace />;
  if (state.memberId === memberId) return <>{children}</>;
  if (state.sessionContextStatus === "loading") return null;
  return state.sessionContext?.capabilities[capability] ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

/** Tela de login não deve abrir para quem já tem sessão — manda pra onde a sessão já permite. */
export function GuestRoute({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === "authenticated") {
    const destination = state.requiresPasswordChange
      ? "/trocar-senha"
      : state.requiresMfaEnrollment
        ? "/mfa/matricula"
        : "/dashboard";
    return <Navigate to={destination} replace />;
  }
  return <>{children}</>;
}
